import { motion } from "framer-motion";
import { METHODIEK } from "@/data/methodiek";
import { PIJLERS, MEETMOMENTEN, ONTWIKKELGEBIEDEN } from "@/data/onderbouwing";

const ease = [0.22, 1, 0.36, 1];
const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-8%" },
  transition: { duration: 0.8, ease },
};

const WEKEN = [
  { w: "Week 1–3", t: "Kennismaken en het vertrekpunt vastleggen", b: ["Startpunt", "Rugzak"] },
  { w: "Week 3–6", t: "Zelfinzicht en bewustwording ontwikkelen", b: ["Rem"] },
  { w: "Week 6–9", t: "Nieuwe vaardigheden ervaren en oefenen", b: ["Vonk", "Speelruimte"] },
  { w: "Week 9–11", t: "Veerkracht, eigen regie en perspectief versterken", b: ["LevelMoment"] },
  { w: "Week 11–12", t: "Een passende volgende stap bepalen", b: ["Volgende beweging"] },
];

const kleur = (naam) => METHODIEK.find((m) => m.naam === naam)?.kleur || "#E06B3F";

export function BegrippenIntro() {
  return (
    <section className="pt-24 sm:pt-28 pb-4" id="begrippen" data-testid="methode-begrippen-intro">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <motion.h2 {...fadeUp} className="lg:col-span-6 font-heading font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
          Zeven woorden <span className="italic text-ll-coral">om even bij stil te staan.</span>
        </motion.h2>
        <motion.div {...fadeUp} className="lg:col-span-6 space-y-4 text-lg text-ll-navy/80 leading-relaxed">
          <p>
            Startpunt, Rugzak, Rem, Vonk, Speelruimte, LevelMoment en Volgende beweging. Het zijn gewone woorden. Ze helpen deelnemers stil te staan
            bij waar ze nu staan, wat ze meedragen, wat ze tegenhoudt, wat ze wél kunnen en wat een volgende stap kan zijn.
          </p>
          <p>Je hoort ze in de groep, in oefeningen en in gesprekken met de trainers. Na een paar weken worden ze een gedeelde taal.</p>
        </motion.div>
      </div>
    </section>
  );
}

export function PijlersEnBegrippen() {
  return (
    <section className="py-24 sm:py-32 bg-[#F0EAD8]" data-testid="methode-pijlers-begrippen">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
        <motion.div {...fadeUp} className="max-w-3xl">
          <p className="text-[10px] uppercase tracking-[0.35em] text-ll-coral">Twee lagen, één methodiek</p>
          <h2 className="mt-6 font-heading font-light text-4xl sm:text-5xl leading-[1.04]">
            Zes pijlers voor het ontwerp. <span className="italic text-ll-coral">Zeven begrippen voor de beleving.</span>
          </h2>
        </motion.div>
        <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <motion.div {...fadeUp} className="rounded-[28px] bg-ll-navy text-ll-cream p-8 sm:p-10">
            <p className="text-[10px] uppercase tracking-[0.3em] text-ll-yellow">De zes pijlers</p>
            <p className="mt-3 font-heading text-2xl">Hoe LevelLeven als programma is ontworpen, en waarin de aanpak zich wil onderscheiden.</p>
            <ul className="mt-8 space-y-3">
              {PIJLERS.map((p) => (
                <li key={p.n} className="flex items-center gap-4 border-b border-white/10 pb-3">
                  <span className="chapter-num text-sm text-ll-coral w-6">{p.n}</span>
                  <span className="text-lg">{p.t.replace("De ", "")}</span>
                </li>
              ))}
            </ul>
          </motion.div>
          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} className="rounded-[28px] bg-white border border-ll-ink/10 p-8 sm:p-10">
            <p className="text-[10px] uppercase tracking-[0.3em] text-ll-coral">De zeven kernbegrippen</p>
            <p className="mt-3 font-heading text-2xl text-ll-ink">De herkenbare leer- en ontwikkeltaal die deelnemers en trainers samen gebruiken.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              {METHODIEK.map((m, i) => (
                <span key={m.naam} className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm border border-ll-ink/10 bg-ll-cream">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: m.kleur }} />
                  <span className="chapter-num text-xs text-ll-navy/50">{i + 1}</span> {m.naam}
                </span>
              ))}
            </div>
            <p className="mt-8 text-sm text-ll-navy/70 leading-relaxed">
              De pijlers beschrijven het programma. De begrippen geven het een gezicht. Hoe ze precies op elkaar aansluiten, werken we verder uit.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export function TwaalfWeken() {
  return (
    <section className="py-24 sm:py-32" data-testid="methode-twaalf-weken">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
        <motion.div {...fadeUp} className="max-w-3xl">
          <p className="text-[10px] uppercase tracking-[0.35em] text-ll-coral">LevelLeven START · 12 weken</p>
          <h2 className="mt-6 font-heading font-light text-4xl sm:text-5xl leading-[1.04]">
            Wat er in twaalf weken <span className="italic text-ll-coral">gebeurt.</span>
          </h2>
          <p className="mt-6 text-ll-navy/80 leading-relaxed">
            Een vaste groep van 20–22 deelnemers, twee trainers, veel oefenen en persoonlijke aandacht. De weken zijn een richtlijn: iedereen ontwikkelt zich
            in een eigen tempo, en een directe stap naar betaald werk is niet voor iedereen het einddoel.
          </p>
        </motion.div>
        <ol className="mt-14 grid grid-cols-1 md:grid-cols-5 gap-4">
          {WEKEN.map((w, i) => (
            <motion.li key={w.w} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.08, ease }} data-testid={`methode-week-${i}`} className="relative rounded-3xl bg-white border border-ll-ink/10 p-6 pt-8 overflow-hidden">
              <span className="absolute top-0 left-0 h-1.5 w-full" style={{ background: kleur(w.b[0]) }} />
              <p className="text-[10px] uppercase tracking-[0.28em] text-ll-navy/60">{w.w}</p>
              <p className="mt-3 font-heading text-xl leading-snug text-ll-ink">{w.t}</p>
              <p className="mt-4 text-xs text-ll-navy/60">{w.b.join(" · ")}</p>
            </motion.li>
          ))}
        </ol>
      </div>
    </section>
  );
}

export function Meetproces() {
  return (
    <section className="py-24 sm:py-32 bg-ll-mist" data-testid="methode-meetproces">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <motion.div {...fadeUp} className="lg:col-span-5">
          <p className="text-[10px] uppercase tracking-[0.35em] text-ll-coral">Ontwikkeling zichtbaar maken</p>
          <h2 className="mt-6 font-heading font-light text-4xl sm:text-5xl leading-[1.04]">
            Meten zonder <span className="italic text-ll-coral">het mens-zijn te vergeten.</span>
          </h2>
          <p className="mt-6 text-ll-navy/80 leading-relaxed">Voorbeelden van ontwikkelgebieden die we volgen:</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {ONTWIKKELGEBIEDEN.map((g) => <span key={g} className="rounded-full bg-white px-4 py-2 text-sm border border-ll-ink/10">{g}</span>)}
          </div>
          <p className="mt-8 text-sm text-ll-navy/70 leading-relaxed" data-testid="methode-meet-disclaimer">
            Eerlijk: de LevelLeven-meetset is nog niet wetenschappelijk gevalideerd. We beschrijven hier het beoogde meetproces, dat we in pilots verder ontwikkelen en toetsen.
          </p>
        </motion.div>
        <ol className="lg:col-span-7 space-y-3">
          {MEETMOMENTEN.map((m, i) => (
            <motion.li key={m.t} initial={{ opacity: 0, x: -12 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }} className="flex gap-5 rounded-2xl bg-white p-6 border border-ll-ink/10">
              <span className="chapter-num text-2xl text-ll-coral w-8">{i + 1}</span>
              <div>
                <p className="font-heading text-xl text-ll-ink">{m.t}</p>
                <p className="mt-1 text-sm text-ll-navy/75">{m.d}</p>
              </div>
            </motion.li>
          ))}
        </ol>
      </div>
    </section>
  );
}
